import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Form, Col, Row } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { NumericFormat as NumberFormat } from "react-number-format";
import DatePicker from "react-datepicker";

import { deepEqual, toDateOrNull } from "../../../utils/object";
import { ConnectedSettingList } from "../../../layout/select/setting-list";
import RateEditor from "./rate-editor";

const mapPropsToState = (props) => ({
  rates: Array.isArray(props.rates) ? props.rates : [],
  representationDate: toDateOrNull(props.representationDate),
  terminationDate: toDateOrNull(props.terminationDate),
  terminationStyle: props.terminationStyle ?? "",
  retainer: props.retainer ?? 0,
  retainerAsOf: toDateOrNull(props.retainerAsOf),
  minutes: props.minutes ?? 6,
});

const Client = forwardRef(function Client(props, ref) {
  const mappedProps = useMemo(
    () => mapPropsToState(props),
    [
      props.rates,
      props.representationDate,
      props.terminationDate,
      props.terminationStyle,
      props.retainer,
      props.retainerAsOf,
      props.minutes,
    ]
  );

  const [client, setClient] = useState(mappedProps);

  const isHydratingRef = useRef(true);
  const prevMappedRef = useRef(mappedProps);
  const initialValueRef = useRef(mappedProps);

  useEffect(() => {
    isHydratingRef.current = false;
  }, []);

  useEffect(() => {
    if (deepEqual(prevMappedRef.current, mappedProps)) return;

    isHydratingRef.current = true;
    setClient(mappedProps);
    prevMappedRef.current = mappedProps;
    initialValueRef.current = mappedProps;

    queueMicrotask(() => {
      isHydratingRef.current = false;
    });
  }, [mappedProps]);

  const updateField = useCallback((name, value) => {
    if (!name) return;
    setClient((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const onChange = useCallback(
    (e) => {
      const name = e?.target?.name;
      const value = e?.target?.value;
      updateField(name, value);
    },
    [updateField]
  );

  const onNumberChange = useCallback(
    (e, name) => {
      updateField(name, e?.floatValue ?? 0);
    },
    [updateField]
  );

  const onRateChange = useCallback(
    (e) => {
      updateField("rates", Array.isArray(e) ? [...e] : []);
    },
    [updateField]
  );

  const validate = useCallback(() => {
    const errors = {};

    if (client.terminationDate && client.representationDate && client.terminationDate < client.representationDate) {
      errors.terminationDate = "Representation end date cannot be before the start date.";
    }

    if (Number(client.retainer ?? 0) < 0) {
      errors.retainer = "Minimum retainer cannot be negative.";
    }

    if (Number(client.minutes ?? 0) <= 0) {
      errors.minutes = "Interval must be greater than zero.";
    }

    return {
      success: Object.keys(errors).length === 0,
      errors,
    };
  }, [client]);

  useImperativeHandle(
    ref,
    () => ({
      getValue: () => ({
        ...client,
        rates: Array.isArray(client.rates) ? [...client.rates] : [],
      }),
      setValue: (value = {}) => {
        const next = mapPropsToState(value);
        setClient(next);
      },
      reset: (value = null) => {
        const next = value ? mapPropsToState(value) : initialValueRef.current;
        setClient(next);
      },
      validate,
      isDirty: () => !deepEqual(initialValueRef.current, client),
    }),
    [client, validate]
  );

  return (
    <>
      <Col xs="12">
        <Row className="g-3">
          <Col lg="12">
            <h2 className="small-title">Representation Information</h2>
          </Col>
        </Row>

        <Row className="g-3">
          <Col md="6">
            <div className="mb-3 top-label">
              <DatePicker
                isClearable
                id="representationDate"
                name="representationDate"
                className="form-control"
                selected={client.representationDate}
                onChange={(date) => updateField("representationDate", date)}
              />
              <Form.Label>REPRESENTATION STARTED</Form.Label>
            </div>
          </Col>

          <Col md="6">
            <div className="mb-3 top-label">
              <DatePicker
                isClearable
                id="terminationDate"
                name="terminationDate"
                className="form-control"
                selected={client.terminationDate}
                onChange={(date) => updateField("terminationDate", date)}
              />
              <Form.Label>REPRESENTATION ENDED</Form.Label>
            </div>
          </Col>

          <Col>
            <div className="mb-3 top-label">
              <ConnectedSettingList
                name="terminationStyle"
                setting="CLIENT_TERMINATION"
                category="Collections"
                value={client.terminationStyle || ""}
                onChange={onChange}
              />
              <Form.Label>TERMINATION REASON</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          <Col md="6">
            <div className="mb-3 top-label">
              <NumberFormat
                displayType="input"
                type="text"
                id="retainer"
                name="retainer"
                className="text-end form-control mb-2"
                prefix="$"
                thousandSeparator={false}
                allowNegative={false}
                value={client.retainer ?? 0}
                onValueChange={(e) => onNumberChange(e, "retainer")}
                isAllowed={({ floatValue }) =>
                  floatValue == null || floatValue <= 500000
                }
              />
              <Form.Label>MINIMUM RETAINER</Form.Label>
            </div>
          </Col>

          <Col md="6">
            <div className="mb-3 top-label">
              <NumberFormat
                displayType="input"
                type="text"
                id="minutes"
                name="minutes"
                className="text-end form-control mb-2"
                thousandSeparator={false}
                allowNegative={false}
                value={client.minutes ?? 6}
                onValueChange={(e) => onNumberChange(e, "minutes")}
                isAllowed={({ floatValue }) =>
                  floatValue == null || floatValue <= 60
                }
              />
              <Form.Label>INTERVAL (MINUTES)</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          <Col>
            <h2 className="small-title">Active Rates</h2>
          </Col>
        </Row>

        <RateEditor onChange={onRateChange} maxValue={50000} rates={client.rates} />
      </Col>
    </>
  );
});

export default Client;